/**
 * Phase 2: Server Architecture
 * Server purposes, read/write characteristics, query safety rules
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Server, Loader2, Check, AlertTriangle, Database, Zap } from 'lucide-react';
import { getConnections, saveServerDescription, getServerDescriptions } from '../../services/api';

const Phase2ServerArchitecture = ({ onComplete, isAdmin }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [serverConfigs, setServerConfigs] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const [conns, savedResp] = await Promise.all([
        getConnections(),
        getServerDescriptions().catch(() => ({ server_descriptions: {} })),
      ]);
      setConnections(conns);

      const saved = savedResp?.server_descriptions || {};

      // Initialize server configs — prefer saved business_setup data, then fall back
      // to anything on the connection record, then to defaults.
      const configs = {};
      conns.forEach(conn => {
        const s = saved[conn.id] || {};
        configs[conn.id] = {
          description: s.description ?? conn.description ?? '',
          purpose: s.purpose ?? conn.purpose ?? '',
          query_type: s.query_type ?? conn.query_type ?? 'mixed',
          is_realtime: s.is_realtime !== undefined
            ? !!s.is_realtime
            : (conn.is_realtime !== false),
          max_query_time: s.max_query_time ?? conn.max_query_time ?? 30,
          avoid_heavy_queries: s.avoid_heavy_queries ?? conn.avoid_heavy_queries ?? false,
          notes: s.notes ?? conn.notes ?? '',
        };
      });
      setServerConfigs(configs);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveServer = async (connId, connName) => {
    const config = serverConfigs[connId];
    if (!config?.description?.trim()) {
      toast.error('Please describe what this server is for');
      return;
    }

    setSaving(prev => ({ ...prev, [connId]: true }));
    try {
      await saveServerDescription(connId, config.description, config.purpose, {
        query_type: config.query_type,
        is_realtime: config.is_realtime,
        max_query_time: config.max_query_time,
        avoid_heavy_queries: config.avoid_heavy_queries,
        notes: config.notes,
      });
      toast.success(`Server "${connName}" configuration saved`);

      // Calculate overall progress
      const total = connections.length;
      const configured = Object.values(serverConfigs).filter(c => c.description?.trim()).length;
      onCompleteRef.current(Math.round((configured / total) * 100));
    } catch (error) {
      toast.error('Failed to save server configuration');
    } finally {
      setSaving(prev => ({ ...prev, [connId]: false }));
    }
  };

  const updateConfig = (connId, field, value) => {
    setServerConfigs(prev => ({
      ...prev,
      [connId]: { ...prev[connId], [field]: value }
    }));
  };

  const getProgress = () => {
    if (connections.length === 0) return 0;
    const configured = Object.values(serverConfigs).filter(c => c.description?.trim()).length;
    return Math.round((configured / connections.length) * 100);
  };

  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (connections.length === 0) {
    return (
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5 text-green-600" />
            Phase 2: Server Architecture
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No database connections configured yet.</p>
          <p className="text-sm text-gray-400 mt-2">Add connections in the Admin Dashboard first.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="w-5 h-5 text-green-600" />
          Phase 2: Server Architecture
          <span className="ml-auto text-sm font-normal text-gray-400">
            {getProgress()}% complete
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-gray-500">
          Describe what each server is used for. This helps the AI know where to look for data and which servers can handle heavy queries.
        </p>

        {connections.map(conn => {
          const config = serverConfigs[conn.id] || {};
          
          return (
            <div key={conn.id} className="p-4 border rounded-lg space-y-4 bg-gray-50">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-500" />
                <span className="font-semibold text-lg">{conn.name}</span>
                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded">
                  {conn.host}:{conn.port}
                </span>
                {conn.database_count > 0 && (
                  <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded">
                    {conn.database_count} databases
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">What is this server for? *</Label>
                  <Textarea
                    value={config.description || ''}
                    onChange={(e) => updateConfig(conn.id, 'description', e.target.value)}
                    placeholder="e.g., This server handles all user transactions, deposits, and withdrawals. It's our primary financial database."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Main purpose/function</Label>
                  <Input
                    value={config.purpose || ''}
                    onChange={(e) => updateConfig(conn.id, 'purpose', e.target.value)}
                    placeholder="e.g., Transaction processing, User accounts, Game records"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Query Type
                  </Label>
                  <Select
                    value={config.query_type || 'mixed'}
                    onValueChange={(v) => updateConfig(conn.id, 'query_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read_heavy">Read Heavy (Analytics)</SelectItem>
                      <SelectItem value="write_heavy">Write Heavy (Live Transactions)</SelectItem>
                      <SelectItem value="mixed">Mixed (Both)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Max Query Time (seconds)</Label>
                  <Input
                    type="number"
                    value={config.max_query_time || 30}
                    onChange={(e) => updateConfig(conn.id, 'max_query_time', parseInt(e.target.value) || 30)}
                    min={5}
                    max={300}
                  />
                </div>
                <div className="space-y-2 flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.avoid_heavy_queries || false}
                      onChange={(e) => updateConfig(conn.id, 'avoid_heavy_queries', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      Avoid heavy queries
                    </span>
                  </label>
                </div>
              </div>

              {isAdmin && (
                <div className="flex justify-end pt-2">
                  <Button 
                    size="sm" 
                    onClick={() => handleSaveServer(conn.id, conn.name)}
                    disabled={saving[conn.id]}
                  >
                    {saving[conn.id] ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default Phase2ServerArchitecture;
